import React from 'react';
import { X, Target, Map, Users, Clock, DollarSign, CheckCircle, ArrowRight, Lightbulb } from 'lucide-react';

interface NiveauxDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NiveauxDecisionModal: React.FC<NiveauxDecisionModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                <Target className="w-8 h-8 text-blue-600" />
                Les 3 Niveaux de Décision TI
              </h2>
              <p className="text-gray-600 mt-1">Guide de Coaching pour l'Équipe BZ</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          
          {/* Introduction */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-blue-500">
            <h3 className="text-xl font-bold text-blue-800 mb-3 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-blue-600" />
              Pourquoi Comprendre les 3 Niveaux ?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Pour l'Équipe :</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Clarté sur les rôles et responsabilités</li>
                  <li>• Évite les malentendus et conflits</li>
                  <li>• Améliore la communication interne</li>
                  <li>• Facilite la prise de décision</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Pour le Client :</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Compréhension claire de qui fait quoi</li>
                  <li>• Confiance dans l'organisation BZ</li>
                  <li>• Satisfaction accrue</li>
                  <li>• Décisions plus rapides</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Les 3 Niveaux Détaillés */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Les 3 Niveaux de Décision</h3>
            
            {/* Niveau Stratégique */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border-l-4 border-purple-500">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  S
                </div>
                <div>
                  <h4 className="text-xl font-bold text-purple-800">Stratégique (Vision)</h4>
                  <p className="text-purple-600">Niveau Direction - Vision long terme</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Caractéristiques
                  </h5>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-purple-500 mt-0.5" />
                      <span><strong>Horizon :</strong> 3-5 ans</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-purple-500 mt-0.5" />
                      <span><strong>Budget :</strong> Global, millions $</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-purple-500 mt-0.5" />
                      <span><strong>Décideurs :</strong> Direction, C-Level</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5" />
                      <span><strong>Impact :</strong> Transformation business</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Map className="w-5 h-5 text-purple-600" />
                    Exemples Concrets
                  </h5>
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-purple-800 text-sm">Transformation Numérique</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Vision globale, budget 5M$, ROI 3-5 ans, orientation business
                      </div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-purple-800 text-sm">Stratégie Cybersécurité</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Priorisation risques business, investissements globaux
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Niveau Tactique */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  T
                </div>
                <div>
                  <h4 className="text-xl font-bold text-green-800">Tactique (Moyen/Plan)</h4>
                  <p className="text-green-600">Niveau Management - Plan détaillé</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Caractéristiques
                  </h5>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-green-500 mt-0.5" />
                      <span><strong>Horizon :</strong> 6-18 mois</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-green-500 mt-0.5" />
                      <span><strong>Budget :</strong> Par projet, centaines K$</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-green-500 mt-0.5" />
                      <span><strong>Décideurs :</strong> Managers, Primes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                      <span><strong>Impact :</strong> Amélioration processus</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Map className="w-5 h-5 text-green-600" />
                    Exemples Concrets
                  </h5>
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-green-800 text-sm">Migration Cloud - Plan</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Plan détaillé, timeline, phases, budget 500K$, 12 mois
                      </div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-green-800 text-sm">Roadmap Innovation IA</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Cas d'usage, plan déploiement par phases, 18 mois
                      </div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-green-800 text-sm">Plan de Continuité</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Processus business, RTO/RPO, budget 200K$, 6 mois
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Niveau Technique */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border-l-4 border-blue-500">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  T
                </div>
                <div>
                  <h4 className="text-xl font-bold text-blue-800">Technique (Exécution)</h4>
                  <p className="text-blue-600">Niveau Opérationnel - Implémentation</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Caractéristiques
                  </h5>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-blue-500 mt-0.5" />
                      <span><strong>Horizon :</strong> 1-6 mois</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-blue-500 mt-0.5" />
                      <span><strong>Budget :</strong> Par tâche, dizaines K$</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-blue-500 mt-0.5" />
                      <span><strong>Décideurs :</strong> Techniciens, Analysts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5" />
                      <span><strong>Impact :</strong> Fonctionnalité spécifique</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Map className="w-5 h-5 text-blue-600" />
                    Exemples Concrets
                  </h5>
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-blue-800 text-sm">Configuration Serveurs</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Exécution technique, monitoring, budget 50K$, 2 mois
                      </div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-blue-800 text-sm">Sécurisation NIST</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Implémentation contrôles, tests, budget 30K$, 1 mois
                      </div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="font-medium text-blue-800 text-sm">Déploiement Application</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Exécution, tests, monitoring, budget 20K$, 3 semaines
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Matrice de Comparaison */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-xl border-l-4 border-gray-500">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-gray-600" />
              Matrice de Comparaison
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800">Critère</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800">Stratégique</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800">Tactique</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800">Technique</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">Horizon Temporel</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700">3-5 ans</td>
                    <td className="px-4 py-3 text-center text-sm text-green-700">6-18 mois</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700">1-6 mois</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">Budget Typique</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700">Millions $</td>
                    <td className="px-4 py-3 text-center text-sm text-green-700">Centaines K$</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700">Dizaines K$</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">Décideurs</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700">Direction, C-Level</td>
                    <td className="px-4 py-3 text-center text-sm text-green-700">Managers, Primes</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700">Techniciens, Analysts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">Impact</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700">Transformation Business</td>
                    <td className="px-4 py-3 text-center text-sm text-green-700">Amélioration Processus</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700">Fonctionnalité Spécifique</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">Fréquence Décisions</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700">Annuelle</td>
                    <td className="px-4 py-3 text-center text-sm text-green-700">Trimestrielle</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700">Hebdomadaire</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Conseils de Coaching */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border-l-4 border-yellow-500">
            <h3 className="text-xl font-bold text-yellow-800 mb-4 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-600" />
              Conseils de Coaching pour l'Équipe
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Questions à Poser :</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>"À quel niveau cette décision appartient-elle ?"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>"Qui doit être consulté avant de décider ?"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>"Quel est l'impact business de cette décision ?"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>"Quel est le budget et l'horizon temporel ?"</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Signaux d'Alerte :</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-red-500 mt-0.5" />
                    <span>Décision technique avec impact stratégique</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-red-500 mt-0.5" />
                    <span>Budget dépassant le niveau de décision</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-red-500 mt-0.5" />
                    <span>Horizon temporel incohérent</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-red-500 mt-0.5" />
                    <span>Décideur inapproprié pour le niveau</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-xl">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Compris !
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NiveauxDecisionModal;
